// © Microsoft Corporation. All rights reserved.
import { CrsV31Endpoint, CrsV3Endpoint, Prong2ManualPcsCacheTreatmentId, Prong2PcsApiUseAkamaiEndpoint, Prong2PcsDurationTreatmentId, RemoveCrsFDHeadTreatment } from "@msnews/experiences-constants";
/**
 * Remove the prefix and suffix of flightInfo and generate a flight list for prong 2.
 * @param flightInfo sample: "f:prg-sp-liveapi,cprg-c-ads-dl;"
 * @returns flight string list
 */
export function getProng2FlightList(flightInfo) {
    if (flightInfo.startsWith("f:")) {
        flightInfo = flightInfo.substring(2);
    }
    if (flightInfo.endsWith(";")) {
        flightInfo = flightInfo.substring(0, flightInfo.length - 1);
    }
    return flightInfo.split(",");
}
/**
 * Check if Git configs should be used
 * @param {string} version - CRS version. Example: v2 or v3 (case sensitive)
 * @param {string} configIndexDocId. Example: cms/api/amp/experienceConfigIndex/BBUsYQa
 * @param {string} apptype. Example: edgeChromium
 * @returns true if git configs are supported
 */
export function shouldUseGitConfigs(version, configIndexDocId, apptype) {
    // If app type is not defined or empty, do not use Git configs.
    // For local dev scenarion of individual experiences running in standalone, app type is blank.
    // Also app type is MUST param in Git configs, if app type is not present, we do not want to attempt Git configs too in prod.
    // If version is not present or it is not v3 and or v3.1, do not support Git configs
    if (!apptype || !version || (version < CrsV3Endpoint)) {
        return false;
    }
    // Return true if this does not have configIndexDocId
    return !configIndexDocId;
}
/**
 * Checks if flight exist (prg-crs31) if it does out new crs version will point to V3.1,
 * otherwise we will point to the value that is in PCS head data. (Service endpoints).
 * @param version CRS Version from PCS head data
 * @param flights flights from current request.
 * @returns version of crs to be used. (V3 or V3.1) V3.1 will be flight based.
 */
export function tryGetCrsVersion(version, flights, appType) {
    try {
        // Excluding weather and fiance app types from this flight b/c they are SSR enabled, therefore not going to afd
        // when making the crs request, which is necessary for this flight.
        return version &&
            flights &&
            flights.includes(RemoveCrsFDHeadTreatment) &&
            (appType !== "weather" /* AppType.Weather */ && appType !== "finance" /* AppType.Finance */) ? CrsV31Endpoint : version;
    }
    catch (error) {
        // flights is not an array
    }
    return version;
}
/**
 * Determine if 1S request should use fdhead QSP.
 */
export function shouldOneServiceUseFdheadQsp(replaceOneServiceQspOverrides, isNtpSsr = false) {
    const { itemOverrides } = replaceOneServiceQspOverrides || {};
    // Check override QSP. Example: item=should1sUseFdheadQsp:true or item=should1sUseFdheadQsp:false
    const itemQsp = itemOverrides && itemOverrides.find(item => item.key === "should1sUseFdheadQsp");
    if (itemQsp && itemQsp.value) {
        return itemQsp.value === "true";
    }
    // NTP SSR do not use fdhead QSP.
    if (isNtpSsr) {
        return false;
    }
    // Debug/test flags
    const qspFlights = itemOverrides && itemOverrides.find(item => item.key === "flights" || item.key.toLowerCase() === "appendflights");
    if (qspFlights && qspFlights.value && qspFlights.value.length > 0) {
        return true;
    }
    // Default
    return false;
}
function hasFlightInPcsResponse(pcsResponse, targetFlight) {
    const flightInfo = pcsResponse?.pcsData?.info || "";
    const flights = flightInfo.substring(2, flightInfo.length - 1).split(",");
    return flights.includes(targetFlight);
}
/**
 * Determine which PCS cache duration to use for Prong2.
 */
export function shouldDisableProng2CustomPcsCacheDuration(pcsResponse) {
    // If the flight is present, do not use the custom cache duration.
    return hasFlightInPcsResponse(pcsResponse, Prong2PcsDurationTreatmentId);
}
/**
 * Determine if Prong2 timeout PCS cache should be disabled.
 */
export function shouldDisableProng2TimeoutPcsCache(flights) {
    return !!(flights && flights.includes(Prong2ManualPcsCacheTreatmentId));
}
/**
 * Determine if we should only check Prong2 flights.
 * @param appType appType
 * @returns
 */
export function shouldOnlyCheckProng2Flights(appType) {
    return appType === "winWidgets" /* AppType.WindowsNewsWidgets */;
}
/**
 * Determine whether PCS requst should use Akamai endpoint
 */
export function shouldProng2PcsApiUseAkamaiEndpoint(pcsResponse) {
    return hasFlightInPcsResponse(pcsResponse, Prong2PcsApiUseAkamaiEndpoint);
}
//# sourceMappingURL=FlightHelpers.js.map